import {
  Component,
  DestroyRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnInit,
  Output
} from '@angular/core';

import { ButtonComponent, InlineFormSchemaComponent, ReportSidePanelViewComponent } from '@mds/ui';
import { MdsIcon, MdsIconsModule } from '@mds/icons';
import { PerfectScrollbarConfigInterface, PerfectScrollbarModule } from 'perfect-scrollbar-angular';
import { MakersiteService } from '@services/makersite.service';
import { ReusableService } from '@services/reusable.service';
import { ToastrService } from '@services/toaster.service';
import { GlobalDataService } from '@services/global-data.service';
import { MatDrawer } from '@angular/material/sidenav';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReportInfo, ReportType, ReportTypes, SidePanelData, TemplateReportModel } from '../report-detail.model';
import { MenuService } from '@services/menu.service';
import { RouterHelperService } from '@services/router-helper.service';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-report-sidepanel',
  standalone: true,
  imports: [
    ReportSidePanelViewComponent,
    PerfectScrollbarModule,
    ButtonComponent,
    InlineFormSchemaComponent,
    MdsIconsModule
  ],
  templateUrl: './report-sidepanel.component.html',
  styleUrls: ['./report-sidepanel.component.scss']
})
export class ReportSidepanelComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  createReportIcon = MdsIcon.PLAY_2;
  copyButtonIcon = MdsIcon.COPY;
  icons = MdsIcon;
  reportData = {
    reportIcon: MdsIcon.APP_ACTIVITY,
    reportSubTitle: 'environment',
    reportName: '',
    infoIcon: MdsIcon.INFO,
    panelCloseIcon: MdsIcon.RIGHT_PANEL_CLOSE,
    chips: [],
    reportDataPanelTitle: 'Data Insights',
    resultPanelTitle: 'API result'
  };

  @Input() drawerRef: MatDrawer;
  @Input() rid: string;

  private _data: SidePanelData;
  @Input() set data(val: SidePanelData) {
    this._data = val;
    this.setInitialData();
  }

  get data() {
    return this._data;
  }

  private _loading: boolean;
  @Input() set loading(value: boolean) {
    this._loading = value;
    this.updateFormState();
  }

  get loading() {
    return this._loading;
  }

  @Output() valueChanged = new EventEmitter<any>();
  destroyRef = inject(DestroyRef);
  expandedItem: number;
  selected_node_type: string;
  config: PerfectScrollbarConfigInterface = {};

  schema: any;
  lines: any[];
  reportValue: any;
  reportUrl: string;
  showPowerBIUrl: boolean;
  formValues: any;
  actions: any;

  templateOrReportData: ReportTypes;
  menuService = inject(MenuService);

  reportInfo: string;
  templateTitle: string;
  constructor(
    private makersiteService: MakersiteService,
    public reusableService: ReusableService,
    private toastrService: ToastrService,
    private globalDataService: GlobalDataService,
    private routerHelperService: RouterHelperService
  ) {}

  ngOnInit(): void {
    this.reusableService.templateOrReportData$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.templateOrReportData = value;

      if (this.templateOrReportData?.type) {
        this.reportData.chips = [
          {
            label: this.templateOrReportData.type,
            icon: MdsIcon.COMPLIANCE_TAG,
            disabled: false,
            removable: false
          }
        ];
        if (this.templateOrReportData.type === ReportType.report) {
          this.reportInfo = (this.templateOrReportData as ReportInfo).description;
        }
        this.setReportIcon();
        this.setMenu();
      }
    });
  }

  updateFormState() {
    if (this.formValues) {
      if (this._loading) {
        this.formValues.disableForm();
      } else {
        this.formValues.enableForm();
      }
    }
  }

  setReportIcon() {
    if (this.templateOrReportData) {
      this.reportData.reportIcon = this.getReportIcon(this.templateOrReportData.report_category);
    }
  }

  setMenu() {
    const { activeApp, centerActions } = this.menuService.menuConfigSubject.getValue();
    let subtitle = null;
    if (this.templateOrReportData.type === ReportType.report) {
      const reportData = this.templateOrReportData as ReportInfo;
      subtitle = reportData.report_category;
    } else {
      const templateData = this.templateOrReportData as TemplateReportModel;
      this.templateTitle = templateData.report_name || this.templateTitle;
      subtitle = `${this.templateTitle} | ${templateData.type}`;
    }
    this.menuService.setMenuConfig({
      activeApp,
      additionalActiveEntity: {
        mainIcon: {
          icon: this.reportData.reportIcon
        },
        rid: this.templateOrReportData?.rid,
        name: this.templateOrReportData?.title,
        subtitle,
        menuTrigger: 'selectReport'
      },
      centerActions
    });
  }

  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent) {
    if (event.keyCode === 27) {
      this.drawerRef.close();
    }
  }

  setInitialData() {
    if (!this.data) return;
    if (this.data.type === 'Report Template') {
      this.init(this.data);
    } else if (this.data && (this.data.name || this.data.display_value)) {
      if (this.data.type || this.data.label || this.data.cardType) {
        this.init(this.data);
      } else {
        this.makersiteService.getTileCards(this.data.rid).subscribe((res) => {
          this.data.type = res[0].card_type_label;
          this.init(this.data);
        });
      }
    } else if (this.data['node-info'] && this.data['node-info'].sourceNode) {
      this.init(this.data);
    } else if (this.data.rid) {
      this.makersiteService.getTileCards(this.data.rid).subscribe((res) => {
        this.data.name = res[0].title;
        this.data.unit = res[0].unit;
        this.data.type = res[0].card_type_label;
        this.init(this.data);
      });
    }
  }

  init(data) {
    let reportName = '';
    this.expandedItem = 0;

    if (data) {
      reportName = data.name || data.display_value || data.title || this.data['node-info'].sourceNode;
      if (reportName.includes('[')) {
        reportName = reportName.split('[')[0];
      }
      this.selected_node_type = data.type || data.label || data.cardType || this.data['node-info'].type_label;
      this.reportData = {
        ...this.reportData,
        reportDataPanelTitle: reportName,
        reportSubTitle: this.selected_node_type
      };
      this.reusableService.setReportData({ ...data, ...this.reportData });
      this.loadPivotManagerPanel(data);
    }
  }

  // pivot report side panel
  loadPivotManagerPanel(data) {
    this.schema = {
      formSchema: data.inputs
    };
    this.actions = data.actions || '';
    this.lines = data.parameters || [];
    const keyValueArray = {};
    this.lines.forEach((data) => {
      const { key, value } = data;
      if (Array.isArray(value)) {
        const convertedArray = value.map((item) => ({
          name: item,
          value: item
        }));
        keyValueArray[key] = convertedArray;
      } else {
        keyValueArray[key] = value;
      }
    });
    const obj = {
      value: keyValueArray,
      formSchema: this.schema.formSchema
    };
    this.globalDataService.setSidepanelData(obj);
    if (data.powerBIUrl) {
      this.reportUrl = data.powerBIUrl;
      this.showPowerBIUrl = true;
    } else {
      this.reportUrl = '';
      this.showPowerBIUrl = false;
    }
  }

  formValueChanged(event) {
    this.formValues = event;
    this.reportValue = this.formValues.value.Inputs;
    const obj = {
      value: this.reportValue,
      formSchema: this.schema.formSchema
    };
    if (this.data.reportRid !== 'background') this.globalDataService.setSidepanelData(obj);
    this.formValues.validate();
  }

  openMenuItem(menuAction: any) {
    this.routerHelperService.openMenuItem(menuAction);
  }

  createReport() {
    if (!this.formValues?.valid) {
      this.formValues.validate();
      return;
    }
    let reportUrl = `${this.data.reportRid}?background_default=true&`;
    for (const i in this.reportValue) {
      if (typeof this.reportValue[i] === 'string') {
        this.reportValue[i] = this.reportValue[i].replace('#', '');
      } else if (Array.isArray(this.reportValue[i])) {
        this.reportValue[i].forEach((e) => {
          let data = '';
          if (typeof e === 'object') {
            data = e.value || e.rid;
          } else if (e) {
            data = typeof e === 'string' ? e.replace('#', '') : e;
          }
          reportUrl += `${i}=${data}&`;
        });
      }

      if (!this.reportValue[i]) continue;
      if (!Array.isArray(this.reportValue[i])) {
        reportUrl += `${i}=${this.reportValue[i]}&`;
      }
    }

    if (reportUrl.endsWith('&')) {
      reportUrl = reportUrl.substring(0, reportUrl.length - 1);
    }

    reportUrl = reportUrl.replace(/#/g, '').replace('template', this.reportValue.reportRid);
    const baseUrl = window.location.origin;
    this.reportUrl = encodeURI(`${baseUrl}/api/v1/report/${reportUrl}&csv=True`);
    this.callReturnEvent({
      type: 'submit-report',
      payload: this.reportValue,
      url: reportUrl
    });
  }

  reCalculateReport() {
    const id = this.route.snapshot.queryParamMap.get('id');
    const rid = id || this.data.backgroundReportRid;
    this.makersiteService.savedReportsAction(rid, 'recalculate').subscribe((res: any) => {
      this.toastrService.showMessage(res.message, 3000);
    });
  }

  copyMSBI(inputElement) {
    inputElement.select();
    document.execCommand('copy');
    inputElement.setSelectionRange(0, 99999);
    this.toastrService.showMessage('Copied link Successfully', 2000);
  }

  callReturnEvent(values?: any) {
    this.valueChanged.emit({
      value: values
    });
  }

  onToggleSidePanel() {
    this.drawerRef.close();
  }

  private getReportIcon(path: string): MdsIcon {
    if (!path) return null;
    const parsedPath = path.replaceAll(' ', '_').toUpperCase();
    return MdsIcon[`REPORT_${parsedPath}`] ?? MdsIcon.REPORT_EMPTY_CATEGORY;
  }
}
